import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, db, storage } from './firebase-config';
import { doc, setDoc } from 'firebase/firestore';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import './EmployeeSetup.css';

// Define grouped steps (generalized for most job seekers)
const steps = [
    {
        section: 'About You',
        questions: [
            { id: 'fullName', type: 'text', label: 'Full name', placeholder: 'e.g., Jane Doe' },
            { id: 'location', type: 'text', label: 'Current location', placeholder: 'City, State/Province or Country' },
            { id: 'mostRecentTitle', type: 'text', label: 'Most recent job title (optional)', placeholder: 'e.g., Sales Associate' },
            { id: 'experienceLevel', type: 'select', label: 'Overall experience', options: ['No experience', '0-1 years', '1-3 years', '3-5 years', '5-10 years', '10+ years'] }
        ]
    },
    {
        section: 'Education',
        questions: [
            { id: 'educationLevel', type: 'select', label: 'Highest education', options: ['High School or GED', 'Associate Degree', "Bachelor's Degree", "Master's Degree", 'Doctorate', 'Trade/Certificate', 'Other'] }
        ]
    },
    {
        section: 'Skills',
        questions: [
            { id: 'skills', type: 'multiselect', label: 'Top skills', options: [
                'Customer Service', 'Sales', 'Communication', 'Teamwork', 'Leadership', 'Problem Solving', 'Time Management', 'Microsoft Office', 'Data Entry', 'Project Management', 'Social Media', 'Driving', 'Caregiving', 'Hospitality', 'Retail', 'Accounting', 'Basic Computer Skills'
            ]}
        ]
    },
    {
        section: 'Job Preferences',
        questions: [
            { id: 'preferredJobTypes', type: 'multiselect', label: 'Job types you’re open to', options: ['Full-time', 'Part-time', 'Contract', 'Temporary', 'Internship', 'Freelance'] },
            { id: 'workLocationPreference', type: 'select', label: 'Work location', options: ['On-site', 'Hybrid', 'Remote', 'Open to any'] },
            { id: 'salaryExpectation', type: 'select', label: 'Target pay range', options: ['Open to discuss', '$30,000 - $50,000', '$50,000 - $80,000', '$80,000 - $120,000', '$120,000+'] },
            { id: 'availability', type: 'select', label: 'When can you start?', options: ['Immediately', 'Within 2 weeks', 'Within 1 month', 'More than 1 month'] }
        ]
    },
    {
        section: 'Industries of Interest',
        questions: [
            { id: 'preferredIndustries', type: 'multiselect', label: 'Industries you like', options: ['Technology', 'Healthcare', 'Finance', 'Education', 'Retail', 'Hospitality', 'Logistics', 'Construction', 'Manufacturing', 'Government', 'Non-profit', 'Other'] }
        ]
    },
    {
        section: 'Resume',
        questions: [
            { id: 'resumeFile', type: 'file', label: 'Upload your resume (PDF or DOCX)', accept: '.pdf,.doc,.docx' }
        ]
    },
    {
        section: 'Career Goals (Optional)',
        questions: [
            { id: 'careerGoals', type: 'textarea', label: 'Share your goals or what you’re looking for', placeholder: 'Tell us what kinds of roles or companies you’re targeting' }
        ]
    }
];

const EmployeeSetup = () => {
    const navigate = useNavigate();
    const [currentStep, setCurrentStep] = useState(0);
    const [transitionDirection, setTransitionDirection] = useState('next');
    const [formData, setFormData] = useState({
        fullName: '',
        location: '',
        mostRecentTitle: '',
        experienceLevel: '',
        educationLevel: '',
        skills: [],
        preferredJobTypes: [],
        workLocationPreference: '',
        salaryExpectation: '',
        availability: '',
        preferredIndustries: [],
        careerGoals: '',
        resumeUrl: ''
    });
    const [resumeFile, setResumeFile] = useState(null);
    const [isUploadingResume, setIsUploadingResume] = useState(false);

    const handleInputChange = (e, question) => {
        const { value } = e.target;
        setFormData(prev => ({
            ...prev,
            [question.id]: value
        }));
    };

    const handleMultiSelectChange = (option, question) => {
        setFormData(prev => {
            const prevArr = prev[question.id] || [];
            let newArr;
            if (prevArr.includes(option)) {
                newArr = prevArr.filter(item => item !== option);
            } else {
                newArr = [...prevArr, option];
            }
            return {
                ...prev,
                [question.id]: newArr
            };
        });
    };

    const handleFileChange = async (file) => {
        if (!file) return;
        setResumeFile(file);
        try {
            setIsUploadingResume(true);
            const user = auth.currentUser;
            const fileRef = storageRef(storage, `resumes/${user?.uid}/${Date.now()}_${file.name}`);
            await uploadBytes(fileRef, file);
            const url = await getDownloadURL(fileRef);
            setFormData(prev => ({ ...prev, resumeUrl: url }));
        } catch (e) {
            console.error('Error uploading resume:', e);
        } finally {
            setIsUploadingResume(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const user = auth.currentUser;
            if (user) {
                // If user selected a file but url isn't set yet, upload now
                if (resumeFile && !formData.resumeUrl) {
                    await handleFileChange(resumeFile);
                }

                await setDoc(doc(db, 'employees', user.uid), {
                    ...formData,
                    userId: user.uid,
                    createdAt: new Date(),
                    updatedAt: new Date()
                });
                navigate('/job-feed');
            }
        } catch (error) {
            console.error('Error saving employee data:', error);
        }
    };

    const handleNext = () => {
        setTransitionDirection('next');
        setCurrentStep(prev => prev + 1);
        const container = document.querySelector('.employee-setup-container');
        if (container) {
            container.scrollIntoView({ behavior: 'instant', block: 'start' });
        }
    };

    const handleBack = () => {
        setTransitionDirection('previous');
        setCurrentStep(prev => prev - 1);
        const container = document.querySelector('.employee-setup-container');
        if (container) {
            container.scrollIntoView({ behavior: 'instant', block: 'start' });
        }
    };

    const totalSteps = steps.length;
    const currentStepObj = steps[currentStep];

    return (
        <div className="employee-setup-container">
            <div className="setup-header">
                <h1>Employee Setup</h1>
                <div className="progress-bar">
                    <div
                        className="progress-bar-fill"
                        style={{ width: `${((currentStep + 1) / totalSteps) * 100}%` }}
                    />
                </div>
            </div>

            <div className="steps-container">
                {steps.map((step, index) => (
                    <div
                        key={index}
                        className={`step ${
                            index === currentStep
                                ? 'active'
                                : index < currentStep
                                ? 'previous'
                                : 'next'
                        }`}
                    >
                        {index === currentStep && (
                            <div className="setup-step">
                                <h2>{step.section}</h2>
                                {step.questions.map((question, qIndex) => (
                                    <div key={qIndex} className="question-container">
                                        <label>{question.label}</label>
                                        {question.type === 'text' && (
                                            <input
                                                type="text"
                                                className="setup-input"
                                                value={formData[question.id] || ''}
                                                onChange={(e) => handleInputChange(e, question)}
                                            />
                                        )}
                                        {question.type === 'select' && (
                                            <select
                                                className="setup-select"
                                                value={formData[question.id] || ''}
                                                onChange={(e) => handleInputChange(e, question)}
                                            >
                                                <option value="">Select an option</option>
                                                {question.options.map((option, oIndex) => (
                                                    <option key={oIndex} value={option}>
                                                        {option}
                                                    </option>
                                                ))}
                                            </select>
                                        )}
                                        {question.type === 'multiselect' && (
                                            <div className="multiselect-container">
                                                {question.options.map((option, oIndex) => (
                                                    <label key={oIndex} className="multiselect-option">
                                                        <input
                                                            type="checkbox"
                                                            checked={formData[question.id]?.includes(option) || false}
                                                            onChange={() => handleMultiSelectChange(option, question)}
                                                        />
                                                        {option}
                                                    </label>
                                                ))}
                                            </div>
                                        )}
                                        {question.type === 'file' && (
                                            <>
                                                <input
                                                    type="file"
                                                    accept={question.accept || '.pdf,.doc,.docx'}
                                                    className="setup-input"
                                                    onChange={(e) => {
                                                        const file = e.target.files && e.target.files[0];
                                                        if (file) handleFileChange(file);
                                                    }}
                                                />
                                                {isUploadingResume && <div style={{ marginTop: 6 }}>Uploading...</div>}
                                                {!isUploadingResume && formData.resumeUrl && (
                                                    <div style={{ marginTop: 6 }}>
                                                        Resume uploaded ✓
                                                    </div>
                                                )}
                                            </>
                                        )}
                                        {question.type === 'textarea' && (
                                            <textarea
                                                className="setup-textarea"
                                                value={formData[question.id] || ''}
                                                onChange={(e) => handleInputChange(e, question)}
                                            />
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                ))}
            </div>

            <div className="setup-buttons">
                {currentStep > 0 && (
                    <button className="setup-button secondary" onClick={handleBack}>
                        Back
                    </button>
                )}
                {currentStep < totalSteps - 1 ? (
                    <button className="setup-button primary" onClick={handleNext}>
                        Next
                    </button>
                ) : (
                    <button className="setup-button primary" onClick={handleSubmit}>
                        Complete Setup
                    </button>
                )}
            </div>
        </div>
    );
};

export default EmployeeSetup;
